import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Divider,
  Typography,
  Row,
  Col,
} from "antd";
import { User, Lock, Mail, Shield, Building2, Pencil } from "lucide-react";
import { useLanguage, UI_TRANSLATIONS } from "../LanguageContext";

const { Text } = Typography;

const UserProfileModal = ({
  visible,
  onClose,
  userdata,
  usertoken,
  requestServerPost,
}) => {
  const { lang } = useLanguage();
  const t = (UI_TRANSLATIONS[lang] || UI_TRANSLATIONS.en).userProfile;

  const [profileForm] = Form.useForm();
  const [pwForm] = Form.useForm();
  const [profileLoading, setProfileLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [profileChanged, setProfileChanged] = useState(false);

  const user = userdata?.user || {};
  const displayName =
    [user.firstname, user.lastname].filter(Boolean).join(" ") ||
    user.username ||
    user.email ||
    "User";
  const initials = displayName.slice(0, 2).toUpperCase();

  const handleUpdateProfile = (values) => {
    if (!user.userid) {
      message.error(t.missingUser);
      return;
    }
    setProfileLoading(true);
    const params = new FormData();
    params.append("userid", user.userid);
    params.append("firstname", (values.firstname || "").trim());
    params.append("lastname", (values.lastname || "").trim());
    // params.append("company", (values.company || "").trim());
    // params.append("email", (values.email || "").trim());

    requestServerPost(
      "update_user",
      params,
      {},
      (response) => {
        setProfileLoading(false);
        if (response.data && response.data.status === "success") {
          message.success(t.profileUpdated);
        } else {
          message.error(response.data?.message || t.profileError);
        }
      },
      usertoken,
      {},
      (error) => {
        setProfileLoading(false);
        console.error("update_user error:", error);
        message.error(t.profileError);
      },
    );
  };

  const handleChangePassword = (values) => {
    if (!user.userid) {
      message.error(t.missingUser);
      return;
    }
    const { oldpassword, newpassword } = values;
    setPwLoading(true);

    const params = {
      userid: user.userid,
      username: user.username,
      oldpassword,
      password: newpassword,
    };

    requestServerPost(
      "change_password",
      params,
      {},
      (response) => {
        setPwLoading(false);
        if (response.data && response.data.status === "success") {
          message.success(t.pwChanged);
          pwForm.resetFields();
        } else {
          message.error(response.data?.message || t.pwError);
        }
      },
      usertoken,
      {},
      (error) => {
        setPwLoading(false);
        console.error("change_password error:", error);
        message.error(t.pwError);
      },
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={480}
      bodyStyle={{ padding: 0 }}
      destroyOnClose
      centered
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
          borderRadius: "8px 8px 0 0",
          padding: "28px 28px 24px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            fontWeight: 700,
            color: "#fff",
            flexShrink: 0,
            boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
          }}
        >
          {initials}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>
            {displayName}
          </div>
          {user.email && (
            <div
              style={{
                fontSize: 13,
                color: "rgba(255,255,255,0.65)",
                marginTop: 2,
              }}
            >
              {user.email}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          padding: "24px 28px 28px",
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {/* Read-only info */}
        <div
          style={{
            background: "#f8fafc",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 20,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <InfoRow
            icon={<User size={14} />}
            label={t.username}
            value={user.username || "---"}
          />
          <InfoRow
            icon={<User size={14} />}
            label={t.firstName}
            value={user.firstname || "---"}
          />
          <InfoRow
            icon={<User size={14} />}
            label={t.lastName}
            value={user.lastname || "---"}
          />
          <InfoRow
            icon={<Mail size={14} />}
            label={t.email}
            value={user.email || "---"}
          />
          <InfoRow
            icon={<Shield size={14} />}
            label={t.role}
            value={user.role || user.usertype || "User"}
          />
        </div>

        {/* Profile edit */}
        <Divider style={{ margin: "0 0 18px" }}>
          <span style={dividerLabelStyle}>
            <Pencil size={12} />
            {t.editProfile}
          </span>
        </Divider>

        <Form
          form={profileForm}
          layout="vertical"
          onFinish={handleUpdateProfile}
          requiredMark={false}
          initialValues={{
            firstname: user.firstname || "",
            lastname: user.lastname || "",
            // company: user.company || "",
            // email: user.email || "",
          }}
          onValuesChange={(_, all) => {
            setProfileChanged(
              (all.firstname ?? "") !== (user.firstname || "") ||
                (all.lastname ?? "") !== (user.lastname || ""),
            );
          }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="firstname"
                label={<span style={labelStyle}>{t.firstName}</span>}
              >
                <Input
                  placeholder={t.firstNamePlaceholder}
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastname"
                label={<span style={labelStyle}>{t.lastName}</span>}
              >
                <Input
                  placeholder={t.lastNamePlaceholder}
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* <Form.Item
            name="company"
            label={<span style={labelStyle}>{t.company}</span>}
          >
            <Input
              prefix={<Building2 size={14} color="#94a3b8" />}
              placeholder={t.companyPlaceholder}
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={<span style={labelStyle}>{t.emailEdit}</span>}
            rules={[{ type: "email", message: t.emailInvalid }]}
          >
            <Input
              prefix={<Mail size={14} color="#94a3b8" />}
              placeholder={t.emailPlaceholder}
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item> */}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={profileLoading}
              disabled={!profileChanged}
              size="large"
              block
              style={primaryBtnStyle}
            >
              {t.saveProfile}
            </Button>
          </Form.Item>
        </Form>

        {/* Password change */}
        <Divider style={{ margin: "24px 0 18px" }}>
          <span style={dividerLabelStyle}>
            <Lock size={12} />
            {t.changePw}
          </span>
        </Divider>

        <Form
          form={pwForm}
          layout="vertical"
          onFinish={handleChangePassword}
          requiredMark={false}
        >
          <Form.Item
            name="oldpassword"
            label={<span style={labelStyle}>{t.currentPw}</span>}
            rules={[{ required: true, message: t.currentPwRequired }]}
          >
            <Input.Password
              placeholder={t.currentPwPlaceholder}
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="newpassword"
                label={<span style={labelStyle}>{t.newPw}</span>}
                rules={[
                  { required: true, message: t.newPwRequired },
                  { min: 6, message: t.newPwMin },
                ]}
              >
                <Input.Password
                  placeholder={t.newPwPlaceholder}
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirmpassword"
                label={<span style={labelStyle}>{t.confirmPw}</span>}
                dependencies={["newpassword"]}
                rules={[
                  { required: true, message: t.confirmPwRequired },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newpassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t.pwMismatch));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder={t.confirmPwPlaceholder}
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={pwLoading}
              size="large"
              block
              style={primaryBtnStyle}
            >
              {t.savePw}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

const labelStyle = { fontSize: 13, fontWeight: 500 };

const dividerLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  color: "#94a3b8",
  fontWeight: 600,
};

const primaryBtnStyle = {
  borderRadius: 8,
  fontWeight: 600,
  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
  border: "none",
  boxShadow: "0 4px 12px rgba(99,102,241,0.35)",
};

const InfoRow = ({ icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <span style={{ color: "#94a3b8", display: "flex", alignItems: "center" }}>
      {icon}
    </span>
    <Text type="secondary" style={{ fontSize: 12, minWidth: 64 }}>
      {label}
    </Text>
    <Text style={{ fontSize: 13, fontWeight: 500, color: "#1e293b" }}>
      {value}
    </Text>
  </div>
);

export default UserProfileModal;
